import moment from "moment";

import {
    AssignmentStatus,
    type Assignment,
    type AssignmentInitialSettings,
} from "@khan/assignments-types";

export const WRITING_COACH_CONTENT_DESCRIPTOR =
    "AIGuideActivity:x1e6d165d54a37115";

export const containsWritingCoachContent = (
    contentDescriptorsList: Array<string>,
): boolean => {
    return contentDescriptorsList.includes(WRITING_COACH_CONTENT_DESCRIPTOR);
};

export function emptyAssignment(): Assignment {
    const now = moment().toDate();

    return {
        id: undefined,
        key: undefined,
        studentList: undefined,
        isDraft: true,
        assignmentStatus: AssignmentStatus.DRAFT,
        students: [],
        assignedDate: now,
        startDate: now,
        dueDate: defaultDueDate(),
        contentDescriptors: [],
        title: "",
        domainId: "",
        courseId: "",
        unitId: "",
        lessonId: "",
        configuredActivityInputs: {},
    };
}

export function getDay(dueDate: Date): Date {
    return moment(dueDate).startOf("day").toDate();
}

export function updateTime(dueDate: Date, newTime: string): Date {
    const dayString = moment(getDay(dueDate)).format("DD/MM/YYYY");
    return moment(`${dayString} ${newTime}`, "DD/MM/YYYY h:mm a").toDate();
}

export const DEFAULT_TIME = "11:59 PM";

export function defaultDueDate(): Date {
    const tomorrow = moment().add(1, "days").toDate();
    return updateTime(tomorrow, DEFAULT_TIME);
}

export function isPastDate(date: Date): boolean {
    return moment(date).isBefore(moment(), "day");
}

/**
 * Determines if a given date is in the past to return the current or the future
 * date as a moment object.
 *
 * In the case the date is in the past, the minDate should be at least the
 * current date, otherwise we will take the input date as the min date.

 *
 * @param {Date} date The date to be used as reference
 * @returns {Moment} Either a moment object with the initial date or with the
 * current date.
 */
export function getMinDate(date: Date): moment.Moment {
    return isPastDate(date) ? moment() : moment(date);
}

/**
 * Gets a valid date that should be set after the startDate
 *
 * @param {Date} startDate The selected start date
 * @param {Date} dueDate The current due date
 * @param {?Date} maxDueDate The maximum due date for the assignment
 * @returns {Date} The valid due date that should be set after startDate.
 */
export function getValidDueDate(
    startDate: Date,
    dueDate: Date,
    maxDueDate?: Date,
): Date {
    // Get either today or startDate as the minDate.
    const minDate = getMinDate(startDate);

    // Make sure that the dueDate is set after the minimum date.
    if (moment(dueDate).isSameOrBefore(minDate)) {
        const newDueDate = minDate.add(2, "day").endOf("day").toDate();

        // If a max due date is given and the new due date is after it,
        // then we will return the max due date.
        if (maxDueDate && moment(newDueDate).isAfter(maxDueDate)) {
            return maxDueDate;
        }

        // If it's before, then we will move the due date 2 days after.
        return newDueDate;
    }

    // Otherwise, return the current date.
    return dueDate;
}

export function getDefaultCourseMasteryDueDate(): Date {
    return updateTime(moment().add(6, "months").toDate(), DEFAULT_TIME);
}

export const getExerciseId = (contentDescriptor: string): string => {
    const ids = contentDescriptor.split(":");
    return ids[ids.length - 1];
};

export const getInitialAssignmentDueDate = (dueDate: string): Date => {
    const dueDateMoment = moment(dueDate);

    // If the due date is in the future, there's no problem.
    if (dueDateMoment.isAfter(moment())) {
        return new Date(dueDate);
    }

    // Otherwise, update the date to be tomorrow, but preserve the time.
    return updateTime(
        // @ts-expect-error - TS2345 - Argument of type 'Moment' is not assignable to parameter of type 'Date'.
        moment().add(1, "day"),
        dueDateMoment.format("h:mm a"),
    );
};

export const updateLocalStoreSettings = (
    settings: AssignmentInitialSettings,
    localStore: any,
    storageKey: string,
) => {
    // Create a working copy our settings, so that we can mutate as needed.
    const newSettings: AssignmentInitialSettings = {...settings};

    const currentSettings = localStore.get(storageKey) || {};
    const currentClassDescriptors = currentSettings.classDescriptors || [];
    const studentListIsSame =
        currentClassDescriptors.length === 1 &&
        newSettings.studentList &&
        newSettings.studentList.descriptor === currentClassDescriptors[0];

    // A little bit of data munging is required, since often the settings
    // being saved are just IDs; they lack the populated data fetched later.

    // Our `studentList` is an object with just an ID at this point;
    // Other details like "name" are filled in later.
    // If we persist this as "studentList", it's ambiguous with the more
    // complete data available in the store.
    if (newSettings.studentList && !studentListIsSame) {
        // if the new studentList is the same as before, we don't want to
        // update classDescriptors or remove studentIds.
        newSettings.classDescriptors = [newSettings.studentList.descriptor];
        delete newSettings.studentList;

        // We also need to delete the saved "student" list, since the
        // class change makes the current student list irrelevant
        delete currentSettings.studentIds;
    }

    if (newSettings.classDescriptors && currentSettings.studentIds) {
        delete currentSettings.studentIds;
    }

    // @ts-expect-error - TS2551 - Property 'students' does not exist on type 'AssignmentInitialSettings'. Did you mean 'studentIds'?
    if (newSettings.students) {
        // @ts-expect-error - TS2551 - Property 'students' does not exist on type 'AssignmentInitialSettings'. Did you mean 'studentIds'?
        newSettings.studentIds = newSettings.students.map(({kaid}) => kaid);
        // @ts-expect-error - TS2551 - Property 'students' does not exist on type 'AssignmentInitialSettings'. Did you mean 'studentIds'?
        delete newSettings.students;
    }

    // NOTE: Previously we persisted a studentListId key instead;
    // this may still exist on some clients
    localStore.set(storageKey, {
        ...currentSettings,
        ...newSettings,
    });
};

export const BuildQualifiedContentDescriptor = (
    domainId: string | null | undefined,
    courseId: string | null | undefined,
    unitId: string | null | undefined,
    lessonId: string | null | undefined,
    contentDescriptor: string,
): string => {
    return `${domainId || ""}:${courseId || ""}:${unitId || ""}:${
        lessonId || ""
    }:${contentDescriptor}`;
};

export function dateOffsetFromDefaultDueDate(date: Date): number {
    return moment(date).diff(moment(defaultDueDate()), "days");
}
